import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { apiFetch, getAuthToken, setAuthToken, setStoredUser, clearUserSession } from '../api';
import { API_BASE } from '../config';

const DEV_AUTH_BYPASS = process.env.REACT_APP_DEV_AUTH_BYPASS === 'true';
const DEV_AUTH_ROLE = process.env.REACT_APP_DEV_AUTH_ROLE || 'admin';
const DEV_AUTH_EMAIL = process.env.REACT_APP_DEV_AUTH_EMAIL || 'dev@localhost';
const DEV_BYPASS_PAUSED_KEY = 'lrbrain-dev-auth-bypass-paused';
const AUTH_PATH_KEY = 'lrbrain-login-auth-path';

const parseApiJsonSafely = async (response) => {
  const raw = await response.text();
  if (!raw) return {};
  try {
    return JSON.parse(raw);
  } catch {
    return { _raw: raw };
  }
};

const getApiErrorMessage = (payload, fallback) => {
  if (!payload) return fallback;
  if (typeof payload.error === 'string' && payload.error.trim()) return payload.error;
  if (typeof payload.message === 'string' && payload.message.trim()) return payload.message;
  if (typeof payload._raw === 'string' && payload._raw.trim()) {
    const cleaned = payload._raw.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    return cleaned.slice(0, 180) || fallback;
  }
  return fallback;
};

export default function GoogleAuthGate({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [isBypassPaused, setIsBypassPaused] = useState(() => {
    try {
      return sessionStorage.getItem(DEV_BYPASS_PAUSED_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const location = useLocation();

  // Allow OAuth callback and assessment results pages through without auth
  const isPublicPath = location.pathname === '/auth/callback' ||
                       location.pathname.startsWith('/results/') ||
                       location.pathname.startsWith('/share/whiteboard/');

  // Check existing token on mount
  useEffect(() => {
    const bypassPaused = (() => {
      try {
        return sessionStorage.getItem(DEV_BYPASS_PAUSED_KEY) === 'true';
      } catch {
        return false;
      }
    })();
    setIsBypassPaused(bypassPaused);

    // Development-only auth bypass for local UI testing
    if (process.env.NODE_ENV === 'development' && DEV_AUTH_BYPASS && !bypassPaused) {
      setStoredUser({
        email: DEV_AUTH_EMAIL,
        display_name: 'Local Dev',
        role: DEV_AUTH_ROLE,
        profile_id: null,
      });
      setIsLoading(false);
      setIsAuthenticated(true);
      return;
    }

    // Skip auth check for public paths
    if (isPublicPath) {
      setIsLoading(false);
      setIsAuthenticated(true); // Allow access to render children
      return;
    }

    const token = getAuthToken();
    if (token) {
      verifyExistingToken(token);
    } else {
      setIsLoading(false);
    }
  }, [isPublicPath]);

  // Handle OAuth callback from URL (for redirect flow fallback)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const authError = urlParams.get('error');

    if (code && !isAuthenticated) {
      handleOAuthCallback(code);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (authError) {
      setError(authError);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  // handleOAuthCallback is intentionally omitted because it is declared below and reads
  // the current pending auth path at execution time for redirect fallback.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const verifyExistingToken = async (token) => {
    try {
      const res = await apiFetch('/api/auth/me', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const userData = await res.json();
        if (userData.email) {
          setStoredUser(userData);
        }
        setIsAuthenticated(true);
      } else {
        clearUserSession();
      }
    } catch {
      clearUserSession();
    } finally {
      setIsLoading(false);
    }
  };

  const getPendingAuthPath = () => {
    try {
      return sessionStorage.getItem(AUTH_PATH_KEY) || 'student';
    } catch {
      return 'student';
    }
  };

  const setPendingAuthPath = (authPath) => {
    try {
      sessionStorage.setItem(AUTH_PATH_KEY, authPath);
    } catch {
      // Ignore storage access issues
    }
  };

  const clearPendingAuthPath = () => {
    try {
      sessionStorage.removeItem(AUTH_PATH_KEY);
    } catch {
      // Ignore storage access issues
    }
  };

  const handleOAuthCallback = async (code, authPath = getPendingAuthPath()) => {
    setIsSigningIn(true);
    setError('');

    try {
      const tokenRes = await fetch(
        `${API_BASE}/api/auth/google/callback`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code, auth_path: authPath })
        }
      );

      const tokenData = await parseApiJsonSafely(tokenRes);

      if (tokenRes.ok && tokenData.success) {
        setAuthToken(tokenData.token);
        setStoredUser(tokenData.user);
        clearPendingAuthPath();
        setIsAuthenticated(true);
      } else {
        setError(getApiErrorMessage(tokenData, 'Authentication failed'));
      }
    } catch (err) {
      setError(err.message || 'Sign-in failed. Please try again.');
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleGoogleSignIn = async (authPath) => {
    setError('');
    if (process.env.NODE_ENV === 'development' && DEV_AUTH_BYPASS && isBypassPaused) {
      try {
        sessionStorage.removeItem(DEV_BYPASS_PAUSED_KEY);
      } catch {
        // Ignore storage access issues
      }
      setIsBypassPaused(false);
      window.location.assign('/');
      return;
    }

    setIsSigningIn(true);
    setPendingAuthPath(authPath);

    try {
      // Step 1: Get Google OAuth URL from backend
      const urlRes = await apiFetch(`/api/auth/google/login?auth_path=${encodeURIComponent(authPath)}`);
      const urlData = await parseApiJsonSafely(urlRes);

      if (!urlData.authorization_url) {
        throw new Error(getApiErrorMessage(urlData, 'Failed to get Google authorization URL'));
      }

      // Step 2: Open Google OAuth in popup
      const popup = window.open(
        urlData.authorization_url,
        'google-auth',
        'width=500,height=600,menubar=no,toolbar=no,location=no,status=no'
      );

      // Check if popup was blocked
      if (!popup) {
        // Fall back to redirect flow
        window.location.href = urlData.authorization_url;
        return;
      }

      // Step 3: Listen for callback message from popup
      const handleMessage = async (event) => {
        // Verify origin for security
        if (event.origin !== window.location.origin) return;

        if (event.data?.type === 'google-auth-callback') {
          window.removeEventListener('message', handleMessage);
          popup?.close();

          const { code, error: authError } = event.data;

          if (authError) {
            setError(authError);
            setIsSigningIn(false);
            return;
          }

          if (code) {
            await handleOAuthCallback(code, authPath);
          } else {
            setError('No authorization code received');
            setIsSigningIn(false);
          }
        }
      };

      window.addEventListener('message', handleMessage);

      // Poll to check if popup was closed without completing auth
      const pollTimer = setInterval(() => {
        try {
          if (popup?.closed) {
            clearInterval(pollTimer);
            window.removeEventListener('message', handleMessage);
            setIsSigningIn(false);
          }
        } catch (e) {
          // Cross-origin policy may block window.closed check - ignore
        }
      }, 1000);

    } catch (err) {
      setError(err.message || 'Sign-in failed. Please try again.');
      setIsSigningIn(false);
    }
  };

  const handleResumeDevBypass = () => {
    try {
      sessionStorage.removeItem(DEV_BYPASS_PAUSED_KEY);
    } catch {
      // Ignore storage access issues
    }
    setIsBypassPaused(false);
    window.location.assign('/');
  };

  if (isLoading) {
    return (
      <div className="auth-gate-loading">
        <div className="auth-gate-spinner"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return children;
  }

  return (
    <div className="auth-gate-overlay">
      <div className="auth-gate-modal">
        <img src="/lrbrain-logo.png" alt="LRB.ai logo" className="auth-gate-logo" />
        <h2>Welcome to LRBrain</h2>
        <p>Choose how you want to sign in.</p>

        {error && (
          <div className="auth-gate-error">{error}</div>
        )}

        <button
          onClick={() => handleGoogleSignIn('student')}
          disabled={isSigningIn}
          className="google-signin-btn"
        >
          {isSigningIn ? (
            'Signing in...'
          ) : (
            <>
              <GoogleIcon />
              Student sign in
            </>
          )}
        </button>

        <button
          onClick={() => handleGoogleSignIn('teacher')}
          disabled={isSigningIn}
          className="google-signin-btn auth-gate-secondary-btn"
        >
          {isSigningIn ? (
            'Signing in...'
          ) : (
            <>
              <GoogleIcon />
              Teacher sign in
            </>
          )}
        </button>

        {process.env.NODE_ENV === 'development' && DEV_AUTH_BYPASS && isBypassPaused && (
          <button
            type="button"
            className="google-signin-btn dev-bypass-resume-btn"
            onClick={handleResumeDevBypass}
          >
            Resume Dev Bypass
          </button>
        )}

        <p className="auth-gate-domain-note">
          Teachers need an existing L.R. Brain staff account. Students can use their Google account.
        </p>
      </div>

      <style>{`
        .auth-gate-overlay {
          position: fixed;
          inset: 0;
          background: linear-gradient(135deg, var(--lrbrain-navy, #1e3a5f) 0%, #0d2847 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
        }

        .auth-gate-loading {
          position: fixed;
          inset: 0;
          background: var(--lrbrain-navy, #1e3a5f);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
        }

        .auth-gate-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid rgba(255, 255, 255, 0.2);
          border-top-color: var(--lrbrain-primary, #1a91da);
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .auth-gate-modal {
          background: var(--lrbrain-white, #ffffff);
          border-radius: 16px;
          padding: 48px 40px;
          max-width: 420px;
          width: 90%;
          text-align: center;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        }

        .auth-gate-logo {
          width: 80px;
          height: auto;
          margin-bottom: 24px;
        }

        .auth-gate-modal h2 {
          font-family: var(--lrbrain-font-headings, 'Inter', sans-serif);
          color: var(--lrbrain-navy, #1e3a5f);
          font-size: var(--lrbrain-text-2xl, 1.5rem);
          font-weight: 700;
          margin-bottom: 8px;
        }

        .auth-gate-modal p {
          color: var(--lrbrain-text-light, #64748b);
          font-size: var(--lrbrain-text-base, 1rem);
          margin-bottom: 24px;
        }

        .auth-gate-error {
          background: #fef2f2;
          color: #dc2626;
          padding: 12px 16px;
          border-radius: 8px;
          font-size: var(--lrbrain-text-sm, 0.875rem);
          margin-bottom: 16px;
          text-align: left;
        }

        .google-signin-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          width: 100%;
          padding: 14px 24px;
          font-size: var(--lrbrain-text-base, 1rem);
          font-weight: 500;
          background: #ffffff;
          color: #3c4043;
          border: 2px solid #dadce0;
          border-radius: 8px;
          cursor: pointer;
          transition: background 0.2s, border-color 0.2s, box-shadow 0.2s;
          font-family: var(--lrbrain-font-body, sans-serif);
        }

        .google-signin-btn:hover:not(:disabled) {
          background: #f8f9fa;
          border-color: #c6c6c6;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .google-signin-btn:active:not(:disabled) {
          background: #f1f3f4;
        }

        .google-signin-btn:disabled {
          background: #f8f9fa;
          color: #9aa0a6;
          cursor: not-allowed;
        }

        .auth-gate-secondary-btn {
          margin-top: 0.75rem;
        }

        .dev-bypass-resume-btn {
          margin-top: 0.75rem;
          background: rgba(255, 255, 255, 0.18);
          border-color: rgba(255, 255, 255, 0.45);
          color: #ffffff;
        }

        .dev-bypass-resume-btn:hover:not(:disabled) {
          background: rgba(255, 255, 255, 0.28);
          border-color: rgba(255, 255, 255, 0.6);
          box-shadow: none;
          transform: none;
        }

        .auth-gate-domain-note {
          color: var(--lrbrain-text-light, #64748b);
          font-size: var(--lrbrain-text-sm, 0.875rem);
          margin-top: 20px;
          margin-bottom: 0;
        }
      `}</style>
    </div>
  );
}

// Google "G" Logo SVG Component
const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);


